import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import { ALL_SLUGS, MODULES, groupModules } from '../config/nav';

/**
 * Picks which modules a staff group can open. One collapsible row per
 * dashboard section, built from MODULES — a module added to the app shows up
 * here automatically, unticked until an administrator grants it.
 *
 * Modelled on click2gro's TitleCardAccessEditor (Job Titles).
 */
export function ModuleAccessEditor({
    value,
    onChange,
    disabled = false,
    locked = false,
}: {
    readonly value: readonly string[];
    readonly onChange: (next: string[]) => void;
    readonly disabled?: boolean;
    /** Super Admin: bypasses permission checks, so there is nothing to tick. */
    readonly locked?: boolean;
}): React.JSX.Element {
    const [expanded, setExpanded] = useState<string | null>(null);
    const selected = new Set(value);
    const sections = groupModules(MODULES);

    if (locked) {
        return (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                {sections.map((section) => (
                    <Box
                        key={section.heading}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            minHeight: 48,
                            '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            {section.heading}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                Always allowed
                            </Typography>
                            <LockIcon sx={{ fontSize: 16 }} />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    function setModules(slugs: readonly string[], allowed: boolean): void {
        const next = new Set(selected);
        for (const slug of slugs) {
            if (allowed) {
                next.add(slug);
            } else {
                next.delete(slug);
            }
        }
        // Rebuild in dashboard order; anything that is no longer a module is dropped.
        onChange(ALL_SLUGS.filter((slug) => next.has(slug)));
    }

    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {sections.map((section) => {
                const shown = section.items.filter((m) => selected.has(m.slug)).length;
                const total = section.items.length;
                const allShown = shown === total;

                let summary = `${String(shown)} of ${String(total)} allowed`;
                if (shown === 0) {
                    summary = 'No access';
                } else if (allShown) {
                    summary = total === 1 ? 'Allowed' : 'All allowed';
                }

                return (
                    <Accordion
                        key={section.heading}
                        disableGutters
                        elevation={0}
                        square
                        expanded={expanded === section.heading}
                        onChange={(_event, isOpen) => {
                            setExpanded(isOpen ? section.heading : null);
                        }}
                        sx={{
                            '&::before': { display: 'none' },
                            '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ '& .MuiAccordionSummary-content': { minWidth: 0 } }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'baseline',
                                    gap: 1,
                                    flex: 1,
                                    minWidth: 0,
                                    pr: 1,
                                }}
                            >
                                <Typography variant="body2" fontWeight={600}>
                                    {section.heading}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ whiteSpace: 'nowrap', color: shown === 0 ? 'text.disabled' : 'text.secondary' }}
                                >
                                    {summary}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            {total > 1 ? (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={allShown}
                                            indeterminate={shown > 0 && !allShown}
                                            disabled={disabled}
                                            onChange={(e) => {
                                                setModules(
                                                    section.items.map((m) => m.slug),
                                                    e.target.checked,
                                                );
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" fontWeight={600}>
                                            Allow all
                                        </Typography>
                                    }
                                />
                            ) : null}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    columnGap: 1,
                                    pl: total > 1 ? 2 : 0,
                                }}
                            >
                                {section.items.map((m) => (
                                    <FormControlLabel
                                        key={m.slug}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selected.has(m.slug)}
                                                disabled={disabled}
                                                onChange={(e) => {
                                                    setModules([m.slug], e.target.checked);
                                                }}
                                            />
                                        }
                                        label={<Typography variant="body2">{m.label}</Typography>}
                                    />
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}
